import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/index';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Card, CardContent } from "@/components/ui/card.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.jsx";
import { Label } from "@/components/ui/label.jsx";
import { 
  Plus, Search, Home, Calendar, Ruler, Trash2, 
  ChevronRight, Sparkles, Loader2, Settings as SettingsIcon
} from 'lucide-react';
import StorageSettings from '@/components/StorageSettings.jsx';
import { format } from 'date-fns';

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  planned: 'bg-blue-100 text-blue-700',
  quoted: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  installed: 'bg-purple-100 text-purple-700'
};

export default function Projects() {
  const [searchQuery, setSearchQuery] = useState('');
  const [newProject, setNewProject] = useState({ name: '', address: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.entities.Project.list('-created_date')
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.entities.Project.create(data),
    onSuccess: (newProject) => {
      console.log('Project created:', newProject);
      queryClient.invalidateQueries(['projects']);
      setDialogOpen(false);
      setNewProject({ name: '', address: '' });
      // Navigate to the new project
      navigate(`/project/${newProject.id}`);
    },
    onError: (error) => {
      console.error('Failed to create project:', error);
      alert('Failed to create project: ' + error.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.entities.Project.delete(id),
    onSuccess: () => queryClient.invalidateQueries(['projects'])
  });

  const filteredProjects = projects.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTotalFeet = (project) => {
    return project.sections?.reduce((sum, s) => sum + (s.length_feet || 0), 0) || 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={createPageUrl('Home')} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img 
                src="/SNO-LightingLogo.png" 
                alt="SNO-Lighting Logo" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-900">SNO-Lighting</h1>
                <p className="text-xs text-slate-500">Outdoor Lighting Planner</p>
              </div>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSettingsOpen(true)}
            >
              <SettingsIcon className="w-4 h-4 mr-2" />
              Storage
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700 shadow-lg shadow-amber-200">
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Project</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Project / Customer Name</Label>
                  <Input
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    placeholder="e.g., Smith Residence"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={newProject.address}
                    onChange={(e) => setNewProject({ ...newProject, address: e.target.value })}
                    placeholder="e.g., 123 Main St, City, State"
                  />
                </div>
                <Button 
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  onClick={() => createMutation.mutate(newProject)}
                  disabled={!newProject.name || createMutation.isPending}
                >
                  {createMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Create Project
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <Home className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-1">No projects yet</h3>
            <p className="text-slate-500 mb-4">Create your first lighting project to get started</p>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredProjects.map((project) => (
              <Card 
                key={project.id} 
                className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <Link to={`/project/${project.id}`}>
                  <div className="h-40 bg-slate-100 relative overflow-hidden">
                    {project.image_url ? (
                      <img 
                        src={project.image_url} 
                        alt={project.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Home className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    <div className="absolute top-3 right-3">
                      <Badge className={statusColors[project.status || 'draft']}>
                        {project.status || 'draft'}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-slate-900 group-hover:text-amber-600 transition-colors">
                          {project.name}
                        </h3>
                        {project.address && (
                          <p className="text-sm text-slate-500 truncate max-w-[200px]">
                            {project.address}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-amber-600 transition-colors" />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      {project.created_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {(() => {
                            try {
                              const date = new Date(project.created_date);
                              return isNaN(date.getTime()) ? 'N/A' : format(date, 'MMM d, yyyy');
                            } catch {
                              return 'N/A';
                            }
                          })()}
                        </div>
                      )}
                      {getTotalFeet(project) > 0 && (
                        <div className="flex items-center gap-1">
                          <Ruler className="w-3 h-3" />
                          {getTotalFeet(project).toFixed(0)}' lighting
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Link>
                <div className="px-4 pb-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 w-full"
                    onClick={(e) => {
                      e.preventDefault();
                      if (confirm('Delete this project?')) {
                        deleteMutation.mutate(project.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Storage Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Storage Settings</DialogTitle>
          </DialogHeader>
          <StorageSettings />
        </DialogContent>
      </Dialog>
    </div>
  );
}